import * as React from 'react';
import type { PromotionPreflight, PromotionTarget } from '@/types/api';
import { ApiError } from '@/lib/api';
import { PROMOTION_TARGET, errorMessage } from '@/lib/copy';
import { Button } from '@/components/ui/button';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader } from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toast';
import { usePreflight, usePromote } from './queries';
import { PreflightReport } from './preflight-report';

/* ---------------------------------------------------------------------------
   The promotion flow: pre-flight, read it, then merge.

   The dialog exists because the two phases are genuinely two decisions. Opening
   it RUNS the pre-flight — a GET that writes nothing — and the expert then reads
   a real answer about real refs before the confirm button is live. There is no
   path here that merges without a token, and no path that reuses a token the
   server has already refused.

   The refusals are rendered IN PLACE rather than as toasts:

     * 409 carries the whole pre-flight in its body, so the report on screen is
       replaced by the server's own, with every blocking reason at once.
     * 422 means the refs moved since the token was issued — someone pushed. The
       only correct response is to pre-flight again, so that is the only control
       offered. Retrying with the stale token is not a thing this UI can do.

   Nothing in here is a retry loop, and there is deliberately no "force".
--------------------------------------------------------------------------- */

export function PromoteDialog({
  chatId,
  target,
  open,
  onOpenChange,
}: {
  chatId: number;
  target: PromotionTarget;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const copy = PROMOTION_TARGET[target];
  const [note, setNote] = React.useState('');
  /** A pre-flight the SERVER sent back with a refusal, which supersedes ours. */
  const [refusal, setRefusal] = React.useState<PromotionPreflight | null>(null);
  const [staleToken, setStaleToken] = React.useState(false);

  const preflight = usePreflight(chatId, target, open);
  const promote = usePromote(chatId, target);

  // Every open is a fresh decision. A pre-flight from thirty seconds ago is
  // about refs that may have moved.
  React.useEffect(() => {
    if (!open) return;
    setRefusal(null);
    setStaleToken(false);
    setNote('');
    void preflight.refetch();
    // Intentionally keyed on `open` alone: re-running on every render of the
    // query object would pre-flight in a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, target]);

  const report = refusal ?? preflight.data ?? null;
  const token = report?.preflight_token ?? null;
  const canMerge = Boolean(report?.can_promote && token) && !staleToken;

  const submit = () => {
    if (!token) return;
    promote.mutate(
      { preflightToken: token, note: note.trim() || undefined },
      {
        onSuccess: () => {
          toast(
            'info',
            `${copy.label} promotion started`,
            'Repos merge in position order, one fully deployed before the next begins. Progress is on the ledger.',
          );
          onOpenChange(false);
        },
        onError: (error) => {
          if (!(error instanceof ApiError)) return;

          if (error.status === 422 || error.code === 'preflight_stale') {
            setStaleToken(true);
            return;
          }

          const serverPreflight = error.preflight;
          if (serverPreflight) {
            setRefusal(serverPreflight);
            return;
          }

          toast('error', 'The promotion was refused', errorMessage(error.code, error.message));
        },
      },
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (promote.isPending && !next) return;
        onOpenChange(next);
      }}
    >
      <DialogContent width="lg" onEscapeKeyDown={(event) => promote.isPending && event.preventDefault()}>
        <DialogHeader title={copy.verb} description={copy.detail} />

        <DialogBody>
          {staleToken ? (
            <div className="border border-warn bg-warn-soft px-3 py-2.5" role="alert">
              <p className="text-sm font-medium text-ink">The refs moved since this pre-flight ran</p>
              <p className="mt-1 text-xs leading-[17px] text-ink-2">
                Someone pushed between the pre-flight and the merge, so the token no longer pins what is on the branch.
                Nothing was merged. Pre-flight again and read the new answer — retrying with the old token would merge
                code that was never reviewed, which is exactly what the token exists to prevent.
              </p>
              <Button
                variant="secondary"
                size="sm"
                className="mt-2"
                loading={preflight.isFetching}
                onClick={() => {
                  setStaleToken(false);
                  setRefusal(null);
                  void preflight.refetch();
                }}
              >
                Re-run pre-flight
              </Button>
            </div>
          ) : preflight.isLoading || (preflight.isFetching && !report) ? (
            <PreflightSkeleton />
          ) : preflight.isError && !refusal ? (
            <div className="border border-danger bg-danger-soft px-3 py-2.5" role="alert">
              <p className="text-sm font-medium text-ink">Pre-flight could not run</p>
              <p className="mt-1 text-xs leading-[17px] text-ink-2">
                {errorMessage(preflight.error.code, preflight.error.message)}
              </p>
              <Button variant="secondary" size="sm" className="mt-2" onClick={() => void preflight.refetch()}>
                Try again
              </Button>
            </div>
          ) : report ? (
            <PreflightReport preflight={report} target={target} />
          ) : null}

          {canMerge ? (
            <Field
              label="Merge note"
              htmlFor="promotion-note"
            >
              <Textarea
                id="promotion-note"
                rows={2}
                value={note}
                onChange={(event) => setNote(event.target.value)}
                placeholder="Why this is going out"
              />
            </Field>
          ) : null}
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" disabled={promote.isPending} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!canMerge}
            loading={promote.isPending}
            onClick={submit}
            title={canMerge ? copy.detail : 'Pre-flight has to pass before anything can merge.'}
          >
            {copy.verb}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PreflightSkeleton() {
  return (
    <div className="space-y-3">
      <Skeleton className="h-3 w-64" />
      <div className="space-y-2 pl-7">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="space-y-1.5">
            <Skeleton className="h-3 w-40" />
            <Skeleton className="h-2.5 w-28" />
          </div>
        ))}
      </div>
      <p className="font-mono text-tiny text-ink-3">
        Fetching every repo and running merge-tree. Nothing is being written.
      </p>
    </div>
  );
}
